import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssessment } from '../context/AssessmentContext';
import BusinessForm from '../components/assessment/BusinessForm';
import QuestionSection from '../components/assessment/QuestionSection';
import { assessmentData } from '../data/questions';
import { calculateResults } from '../utils/scoring';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAlertCircle } = FiIcons;

export default function Assessment() {
  const { currentStep, setCurrentStep, answers, saveSubmission } = useAssessment();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < assessmentData.length) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Finalizar y Guardar
      const cleanAnswers = {};
      Object.keys(answers).forEach(k => {
        // Manejamos tanto si es un objeto {value, label} como si es un valor directo
        cleanAnswers[k] = typeof answers[k] === 'object' ? answers[k].value : answers[k];
      });
      
      const results = calculateResults(cleanAnswers);
      const newId = saveSubmission(results);
      
      // Navegamos directamente al ID del reporte para evitar que "desaparezca" al refrescar
      navigate(`/results/${newId}`);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const progressPercentage = (currentStep / (assessmentData.length)) * 100;

  return (
    <div className="min-h-screen bg-slate-950 py-12 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        
        {/* Progress Header */}
        <div className="mb-12">
          <div className="flex justify-between items-end mb-4">
            <div>
              <h1 className="text-2xl font-bold text-white">
                {currentStep === 0 ? 'Paso 1: Información de contacto' : `Paso ${currentStep + 1} de ${assessmentData.length + 1}`}
              </h1>
              {currentStep > 0 && (
                <p className="text-blue-400 font-medium text-sm mt-1">{assessmentData[currentStep - 1].title}</p>
              )}
            </div>
            <span className="text-sm font-bold text-slate-500 bg-slate-900 px-3 py-1 rounded-full border border-slate-800">
              {Math.round(progressPercentage)}% completado
            </span>
          </div>
          <div className="w-full h-2.5 bg-slate-900 rounded-full overflow-hidden border border-slate-800">
            <div 
              className="h-full bg-blue-500 transition-all duration-700 ease-out shadow-[0_0_15px_rgba(59,130,246,0.5)]" 
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Disclaimer */}
        {currentStep === 0 && (
          <div className="mb-8 bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 flex gap-4 items-start">
            <SafeIcon icon={FiAlertCircle} className="text-blue-400 text-xl flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-400 leading-relaxed italic">
              Este diagnóstico tiene fines educativos e informativos. No constituye una auditoría oficial, certificación, asesoría legal ni garantía de cumplimiento. Su propósito es ayudarte a identificar áreas de mejora en la seguridad de tu red.
            </p>
          </div>
        )}

        {/* Content Area */}
        <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-800 p-8 sm:p-12">
          {currentStep === 0 ? (
            <BusinessForm onNext={handleNext} />
          ) : (
            <QuestionSection 
              sectionIndex={currentStep - 1} 
              onNext={handleNext} 
              onPrev={handlePrev} 
              isLast={currentStep === assessmentData.length} 
            />
          )}
        </div>
      </div>
    </div>
  );
}